import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select, { mergeStyles, components as selectComponents } from "react-select";
import AsyncSelect from "react-select/async";
import AsyncCreatableSelect from "react-select/async-creatable";
import CreatableSelect from "react-select/creatable";

const TABLE_MIN_HEIGHT = 260;
const FALLBACK_MENU_HEIGHT = 150;
const ignoreInputChange = () => {};
const renderNothing = () => null;

function AccessibleInput(props) {
  const disabled = Boolean(props.isDisabled);
  const searchable = props.selectProps.legacyIsSearchable;
  return (
    <selectComponents.Input
      {...props}
      aria-describedby={props.selectProps.legacyAriaDescribedBy || props["aria-describedby"]}
      aria-disabled={disabled || undefined}
      aria-readonly={!searchable || undefined}
      aria-required={props.selectProps.legacyAriaRequired || undefined}
      isDisabled={false}
      inputMode={searchable ? props.inputMode : "none"}
      onChange={searchable ? props.onChange : ignoreInputChange}
      readOnly={disabled || undefined}
      tabIndex={disabled ? -1 : props.tabIndex}
    />
  );
}

const baseStyles = {
  clearIndicator: (base) => ({
    ...base,
    padding: 7,
  }),
  control: (base, state) => {
    const invalid = state.selectProps["aria-invalid"] === true || state.selectProps["aria-invalid"] === "true";
    const borderColor = invalid ? "#a94442" : state.isFocused ? "#66afe9" : "#d9d9d9";
    return {
      ...base,
      minHeight: 36,
      borderColor,
      boxShadow: state.isFocused
        ? invalid
          ? "inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 3px rgba(169, 68, 66, 0.1)"
          : "0 0 0 1px #66afe9"
        : "none",
      "&:hover": {
        borderColor: invalid ? "#a94442" : state.isFocused ? "#66afe9" : "#b3b3b3",
      },
    };
  },
  dropdownIndicator: (base, state) => ({
    ...base,
    color:
      state.selectProps["aria-invalid"] === true || state.selectProps["aria-invalid"] === "true"
        ? "#a94442"
        : base.color,
    padding: 7,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    minHeight: 34,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    paddingBottom: 0,
    paddingTop: 0,
    // react-select focuses this input whenever the control is tapped, even when
    // the select is not searchable. Below 16px iOS zooms the viewport on that
    // focus and never zooms back out, so every select on a phone left the page
    // magnified. The floor has to live here rather than in the stylesheet
    // because react-select's own emotion rule wins the cascade against it.
    "@media (max-width: 767px)": {
      fontSize: 16,
    },
  }),
  menu: (base) => ({
    ...base,
    marginBottom: 1,
    marginTop: 1,
    zIndex: 10,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 1060,
  }),
  option: (base) => ({
    ...base,
    padding: "8px 10px",
  }),
  placeholder: (base, state) => ({
    ...base,
    color:
      state.selectProps["aria-invalid"] === true || state.selectProps["aria-invalid"] === "true" ? "#a94442" : "#aaa",
  }),
  singleValue: (base, state) => ({
    ...base,
    color:
      state.selectProps["aria-invalid"] === true || state.selectProps["aria-invalid"] === "true" ? "#a94442" : "#333",
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: 34,
    padding: "2px 8px",
  }),
};

function useTableMarginOnOpen(containerRef) {
  const tableRef = useRef(null);
  const animationFrameRef = useRef(null);

  const onClose = useCallback(() => {
    if (animationFrameRef.current !== null) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    const table = tableRef.current;
    if (table) {
      table.style.removeProperty("margin-bottom");
      tableRef.current = null;
    }
  }, []);

  const onOpen = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;
    const table = element.closest("table");
    if (!table) return;
    tableRef.current = table;
    animationFrameRef.current = window.requestAnimationFrame(() => {
      animationFrameRef.current = null;
      const tableHeight = table.getBoundingClientRect().height;
      if (tableHeight >= TABLE_MIN_HEIGHT) return;
      const menu = element.querySelector(".Select__menu");
      if (!menu) {
        table.style.setProperty("margin-bottom", `${FALLBACK_MENU_HEIGHT}px`, "important");
        return;
      }
      const clipContainer = element.closest(".table-responsive") || table.parentElement;
      const containerRect = clipContainer.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const overflow = menuRect.bottom - containerRect.bottom + 28;
      const marginToAdd = Math.max(0, Math.ceil(overflow));
      if (marginToAdd > 0) {
        table.style.setProperty("margin-bottom", `${marginToAdd}px`, "important");
      }
    });
  }, [containerRef]);

  useEffect(() => onClose, [onClose]);

  return { onOpen, onClose };
}

function optionValue(option, valueKey, getOptionValue) {
  if (option === null || option === undefined) return option;
  if (getOptionValue) return getOptionValue(option);
  if (typeof option !== "object") return option;
  return option[valueKey];
}

function optionLabel(option, labelKey, getOptionLabel) {
  if (option === null || option === undefined) return "";
  if (getOptionLabel) return getOptionLabel(option);
  if (typeof option !== "object") return String(option);
  const label = option[labelKey] ?? option.label ?? option[valueKeyForLabelFallback(option)];
  return label === null || label === undefined ? "" : String(label);
}

function valueKeyForLabelFallback(option) {
  return "value" in option ? "value" : Object.keys(option)[0];
}

function flatOptions(options) {
  return (options || []).flatMap((option) => (Array.isArray(option?.options) ? flatOptions(option.options) : [option]));
}

function primitiveMatches(left, right) {
  if (left === right) return true;
  if (left === null || left === undefined || right === null || right === undefined) return false;
  return String(left) === String(right);
}

function selectedOption(value, options, valueKey, getOptionValue) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object") return value;
  const match = flatOptions(options).find((option) =>
    primitiveMatches(optionValue(option, valueKey, getOptionValue), value),
  );
  if (match) return match;
  return null;
}

function selectedValue(value, options, isMulti, valueKey, getOptionValue) {
  if (isMulti) {
    const values = Array.isArray(value) ? value : value === null || value === undefined || value === "" ? [] : [value];
    return values.map((item) => selectedOption(item, options, valueKey, getOptionValue)).filter(Boolean);
  }
  return selectedOption(value, options, valueKey, getOptionValue);
}

function loadedOptions(result) {
  if (Array.isArray(result)) return result;
  return Array.isArray(result?.options) ? result.options : [];
}

function legacyLoadOptions(loadOptions, onLoaded) {
  if (!loadOptions) return undefined;
  let latestRequest = 0;
  return (inputValue, callback) => {
    const requestId = ++latestRequest;
    let callbackUsed = false;
    const storeLatestOptions = (options) => {
      if (requestId === latestRequest) {
        onLoaded(options);
      }
    };
    const legacyCallback = (result) => {
      callbackUsed = true;
      const options = loadedOptions(result);
      storeLatestOptions(options);
      callback(options);
    };
    const result = loadOptions(inputValue, legacyCallback);
    if (result && typeof result.then === "function") {
      return result.then((resolvedResult) => {
        const options = loadedOptions(resolvedResult);
        if (!callbackUsed) storeLatestOptions(options);
        return options;
      });
    }
    if (!callbackUsed && result !== undefined) {
      const options = loadedOptions(result);
      storeLatestOptions(options);
      return Promise.resolve(options);
    }
    return undefined;
  };
}

function rootStateClasses(state, isMulti) {
  return [
    state.isFocused && "is-focused",
    state.selectProps.menuIsOpen && "is-open",
    state.hasValue && "has-value",
    isMulti ? "Select--multi" : "Select--single",
  ]
    .filter(Boolean)
    .join(" ");
}

function LegacySelect({ component: Component, forwardedRef, isAsync = false, isCreatable = false, ...legacyProps }) {
  const hasDefaultValueProp = "defaultValue" in legacyProps;
  const hasValueProp = "value" in legacyProps;
  const {
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
    "aria-required": ariaRequired,
    autoload,
    cache,
    cacheOptions,
    className,
    classNames,
    clearable,
    closeOnSelect,
    defaultValue,
    defaultOptions,
    disabled,
    filterOption,
    getOptionLabel,
    getOptionValue,
    id,
    inputProps,
    isClearable,
    isDisabled,
    isMulti,
    isSearchable,
    labelKey = "label",
    loadOptions,
    menuContainerStyle,
    menuPlacement,
    multi,
    onClose,
    onCreateOption,
    onInputChange,
    onNewOptionClick,
    onOpen,
    options = [],
    prompt,
    searchable,
    styles,
    value,
    valueKey = "value",
    visualRedact,
    ...props
  } = legacyProps;
  const containerRef = useRef(null);
  const [asyncOptions, setAsyncOptions] = useState([]);
  const { onOpen: adjustTableOnOpen, onClose: resetTableOnClose } = useTableMarginOnOpen(containerRef);
  const inputId = id || inputProps?.id;
  const multiValue = isMulti ?? multi ?? false;
  const resolvedIsDisabled = isDisabled ?? disabled ?? false;
  const normalizedOptions = useMemo(() => options || [], [options]);
  const valueOptions = useMemo(
    () => [
      ...normalizedOptions,
      ...(Array.isArray(defaultOptions) ? defaultOptions : []),
      ...(isAsync ? asyncOptions : []),
    ],
    [asyncOptions, defaultOptions, isAsync, normalizedOptions],
  );
  const normalizedValue = useMemo(
    () => selectedValue(value, valueOptions, multiValue, valueKey, getOptionValue),
    [getOptionValue, multiValue, value, valueKey, valueOptions],
  );
  const normalizedDefaultValue = useMemo(
    () => selectedValue(defaultValue, valueOptions, multiValue, valueKey, getOptionValue),
    [defaultValue, getOptionValue, multiValue, valueKey, valueOptions],
  );
  const normalizedLoadOptions = useMemo(() => legacyLoadOptions(loadOptions, setAsyncOptions), [loadOptions]);
  const normalizedStyles = useMemo(() => {
    const legacyStyles = menuContainerStyle
      ? {
          menu: (base) => ({
            ...base,
            ...menuContainerStyle,
          }),
        }
      : {};
    return mergeStyles(mergeStyles(baseStyles, legacyStyles), styles || {});
  }, [menuContainerStyle, styles]);
  const normalizedFilterOption = useMemo(
    () => (filterOption ? (candidate, inputValue) => filterOption(candidate.data ?? candidate, inputValue) : undefined),
    [filterOption],
  );
  const normalizedComponents = useMemo(
    () => ({
      ...props.components,
      Input: props.components?.Input || AccessibleInput,
      // react-select's MultiValue renders Remove unconditionally and its
      // removeProps.onClick calls removeValue with no isDisabled check, so a
      // disabled multi select's chips stay clickable.
      //
      // Drop the affordance rather than render an inert one: a control that
      // cannot be operated should not advertise that it can. This deliberately
      // overrides a caller-supplied MultiValueRemove too. A rendering override
      // is not allowed to reopen a safety property, and the capture-phase
      // blocker below does not cover this path -- removeProps is wired to
      // onClick and onTouchEnd, neither of which is a mousedown or touchstart.
      ...(resolvedIsDisabled && multiValue ? { MultiValueRemove: renderNothing } : {}),
    }),
    [multiValue, props.components, resolvedIsDisabled],
  );
  const normalizedClassNames = useMemo(
    () => ({
      ...classNames,
      container: (state) =>
        [rootStateClasses(state, multiValue), classNames?.container?.(state)].filter(Boolean).join(" "),
    }),
    [classNames, multiValue],
  );

  const handleOpen = useCallback(() => {
    adjustTableOnOpen();
    onOpen?.();
  }, [adjustTableOnOpen, onOpen]);

  const handleClose = useCallback(() => {
    resetTableOnClose();
    onClose?.();
  }, [onClose, resetTableOnClose]);

  const handleCreate = useMemo(() => {
    if (onCreateOption) return onCreateOption;
    if (onNewOptionClick) return (inputValue) => onNewOptionClick(inputValue);
    return undefined;
  }, [onCreateOption, onNewOptionClick]);

  const selectProps = {
    ...props,
    "aria-describedby": ariaDescribedBy || inputProps?.["aria-describedby"],
    "aria-invalid": ariaInvalid || inputProps?.["aria-invalid"],
    "aria-label": ariaLabel || inputProps?.["aria-label"],
    "aria-labelledby": ariaLabelledBy || inputProps?.["aria-labelledby"],
    "aria-required": ariaRequired || inputProps?.["aria-required"],
    cacheOptions: cacheOptions ?? cache,
    className: ["Select", className].filter(Boolean).join(" "),
    classNamePrefix: "Select",
    classNames: normalizedClassNames,
    closeMenuOnSelect: closeOnSelect,
    components: normalizedComponents,
    filterOption: normalizedFilterOption,
    getOptionLabel: (option) => optionLabel(option, labelKey, getOptionLabel),
    getOptionValue: (option) => String(optionValue(option, valueKey, getOptionValue) ?? ""),
    inputId,
    instanceId: inputId,
    isClearable: isClearable ?? clearable ?? true,
    isDisabled: resolvedIsDisabled,
    isMulti: multiValue,
    isOptionDisabled: props.isOptionDisabled || ((option) => Boolean(option?.disabled || option?.isDisabled)),
    isSearchable: true,
    legacyAriaDescribedBy: ariaDescribedBy || inputProps?.["aria-describedby"],
    legacyAriaRequired: ariaRequired || inputProps?.["aria-required"],
    legacyIsSearchable: isSearchable ?? searchable ?? true,
    menuPlacement: menuPlacement || "auto",
    onCreateOption: handleCreate,
    onInputChange,
    onMenuClose: handleClose,
    onMenuOpen: handleOpen,
    options: normalizedOptions,
    placeholder: prompt === false ? null : props.placeholder,
    ref: forwardedRef,
    styles: normalizedStyles,
  };

  // A disabled select must be inert to the pointer, not only to the keyboard.
  // react-select 5.10.2 guards onKeyDown with isDisabled but neither
  // onControlMouseDown nor onInputFocus checks it, so a mousedown on the
  // control sets openAfterFocus, focus opens the menu, and an option click
  // commits through onChange. AccessibleInput passes isDisabled={false} to the
  // Input to keep a disabled select named and addressable, which is why the
  // browser's own disabled semantics do not stop this. onDropdownIndicatorMouseDown
  // does check isDisabled but returns without preventDefault, so that mousedown
  // bubbles into onControlMouseDown and opens the menu too.
  //
  // Closing openMenuOnClick and openMenuOnFocus removes both routes into
  // openMenu, and the controlled menuIsOpen makes the closed state independent
  // of any future react-select internal path. All three are no-ops for an
  // enabled select.
  //
  // This is NOT made redundant by the capture-phase blocker on the container
  // span below. That blocker only intercepts mousedown and touchstart, but
  // react-select routes a tap through onControlTouchEnd into onControlMouseDown,
  // and React registers touchstart as a passive listener so the blocker's
  // preventDefault is a no-op there. On a touch device this block is the only
  // thing holding a disabled select shut. The contract test that pins it reads
  // these props off selectProps directly, because the blocker would otherwise
  // mask their removal from every behavioural test.
  if (resolvedIsDisabled) {
    selectProps.menuIsOpen = false;
    selectProps.openMenuOnClick = false;
    selectProps.openMenuOnFocus = false;
  }

  if (hasValueProp) {
    selectProps.value = normalizedValue;
  }
  if (hasDefaultValueProp && defaultValue !== undefined) {
    selectProps.defaultValue = normalizedDefaultValue;
  }
  if (isAsync) {
    selectProps.defaultOptions = defaultOptions ?? autoload ?? true;
    selectProps.loadOptions = normalizedLoadOptions;
  }
  if (!isCreatable) {
    delete selectProps.onCreateOption;
  }
  if (closeOnSelect === undefined) {
    delete selectProps.closeMenuOnSelect;
  }

  const blockDisabledPointer = selectProps.isDisabled
    ? (event) => {
        event.preventDefault();
        event.stopPropagation();
      }
    : undefined;

  return (
    <span
      ref={containerRef}
      data-visual-redact={visualRedact}
      data-visual-redact-control={visualRedact ? "select" : undefined}
      onMouseDownCapture={blockDisabledPointer}
      onTouchStartCapture={blockDisabledPointer}
    >
      <Component {...selectProps} />
    </span>
  );
}

export const SelectWrapper = React.forwardRef(function SelectWrapper(props, ref) {
  return <LegacySelect component={Select} forwardedRef={ref} {...props} />;
});

export const AsyncSelectWrapper = React.forwardRef(function AsyncSelectWrapper(props, ref) {
  return <LegacySelect component={AsyncSelect} forwardedRef={ref} isAsync {...props} />;
});

export const CreatableSelectWrapper = React.forwardRef(function CreatableSelectWrapper(props, ref) {
  return <LegacySelect component={CreatableSelect} forwardedRef={ref} isCreatable {...props} />;
});

export const AsyncCreatableSelectWrapper = React.forwardRef(function AsyncCreatableSelectWrapper(props, ref) {
  return <LegacySelect component={AsyncCreatableSelect} forwardedRef={ref} isAsync isCreatable {...props} />;
});
